/**
 * AI Sections Service
 *
 * Fetches AI section configurations from Supabase for embedding
 * predefined questions on client pages.
 */

import type { AISectionConfig, AISectionStyling } from '../components/AISections';
import type { Database } from '../types/database.generated';
import { logger } from '../utils/core/logger';
import { isSupabaseRequestCancelled } from '../utils/core/supabaseCancelDetection';
import { normalizeDomain } from '../utils/domain/domain';
import { getSupabase } from './supabase';

export type AISectionRow = Database['config']['Tables']['ai_sections']['Row'];

/** Extended config with metadata for listing/selection */
export interface AISectionWithMeta extends AISectionConfig {
    sectionId: string;
    enabled: boolean;
}

/**
 * Fetch an AI section configuration by domain and section ID
 *
 * @param domain - The site domain (will be normalized)
 * @param sectionId - The section identifier (e.g., "pricing-faq", "homepage-hero")
 * @param language - Optional language code for localized questions (default: 'en')
 * @returns The section config if found and enabled, null otherwise
 */
export async function getAISection(
    domain: string,
    sectionId: string,
    language?: string,
    options?: { includeDisabled?: boolean }
): Promise<AISectionConfig | null> {
    const normalizedDomain = normalizeDomain(domain);

    try {
        let query = getSupabase()
            .schema('config')
            .from('ai_sections')
            .select('*')
            .eq('site_domain', normalizedDomain)
            .eq('section_id', sectionId);

        if (!options?.includeDisabled) {
            query = query.eq('enabled', true);
        }

        const { data, error } = await query.single();

        if (error) {
            if (error.code === 'PGRST116') {
                // No rows returned - section doesn't exist or is disabled
                logger.debug('AI section not found or disabled', {
                    domain: normalizedDomain,
                    sectionId,
                });
                return null;
            }
            if (isSupabaseRequestCancelled(error)) {
                logger.debug('AI section fetch cancelled by navigation/unmount', {
                    domain: normalizedDomain,
                    sectionId,
                });
                return null;
            }
            logger.error('Failed to fetch AI section', error, {
                domain: normalizedDomain,
                sectionId,
            });
            return null;
        }

        if (!data) {
            return null;
        }

        return transformRowToConfig(data as AISectionRow, language);
    } catch (error) {
        logger.error('Error fetching AI section', error, {
            domain: normalizedDomain,
            sectionId,
        });
        return null;
    }
}

/**
 * Fetch all enabled AI sections for a domain
 *
 * @param domain - The site domain (will be normalized)
 * @param language - Optional language code for localized questions (default: 'en')
 * @returns Array of section configs (empty if none found)
 */
export async function getAISectionsForDomain(
    domain: string,
    language?: string,
    options?: { includeDisabled?: boolean }
): Promise<AISectionConfig[]> {
    const normalizedDomain = normalizeDomain(domain);

    try {
        let query = getSupabase()
            .schema('config')
            .from('ai_sections')
            .select('*')
            .eq('site_domain', normalizedDomain);

        if (!options?.includeDisabled) {
            query = query.eq('enabled', true);
        }

        const { data, error } = await query.order('created_at', { ascending: true });

        if (error) {
            if (isSupabaseRequestCancelled(error)) {
                logger.debug('AI sections fetch cancelled by navigation/unmount', {
                    domain: normalizedDomain,
                });
                return [];
            }
            logger.error('Failed to fetch AI sections for domain', error, {
                domain: normalizedDomain,
            });
            return [];
        }

        if (!data || data.length === 0) {
            logger.debug('No AI sections found for domain', {
                domain: normalizedDomain,
            });
            return [];
        }

        return (data as AISectionRow[]).map((row) => transformRowToConfig(row, language));
    } catch (error) {
        logger.error('Error fetching AI sections for domain', error, {
            domain: normalizedDomain,
        });
        return [];
    }
}

/**
 * List all AI sections for a domain with metadata (for admin/test UI)
 * Includes disabled sections unlike getAISectionsForDomain
 *
 * @param domain - The site domain (will be normalized)
 * @param language - Optional language code for localized questions (default: 'en')
 * @returns Array of sections with metadata (empty if none found)
 */
export async function listAISectionsForDomain(
    domain: string,
    language?: string
): Promise<AISectionWithMeta[]> {
    const normalizedDomain = normalizeDomain(domain);

    try {
        const { data, error } = await getSupabase()
            .schema('config')
            .from('ai_sections')
            .select('*')
            .eq('site_domain', normalizedDomain)
            .order('created_at', { ascending: true });

        if (error) {
            logger.error('Failed to list AI sections for domain', error, {
                domain: normalizedDomain,
            });
            return [];
        }

        if (!data || data.length === 0) {
            logger.debug('No AI sections found for domain', {
                domain: normalizedDomain,
            });
            return [];
        }

        return (data as AISectionRow[]).map((row) => transformRowToConfigWithMeta(row, language));
    } catch (error) {
        logger.error('Error listing AI sections for domain', error, {
            domain: normalizedDomain,
        });
        return [];
    }
}

/** Styling JSON structure stored in the database */
interface StylingJson {
    primaryColor?: string;
    primary_color?: string;
    backgroundColor?: string;
    background_color?: string;
    buttonBackground?: string;
    button_background?: string;
    buttonTextColor?: string;
    button_text_color?: string;
    buttonFontSize?: string;
    button_font_size?: string;
    buttonFontWeight?: string;
    button_font_weight?: string;
    iconSize?: number;
    icon_size?: number;
    arrowSize?: number;
    arrow_size?: number;
    titleColor?: string;
    title_color?: string;
    gap?: string;
    layout?: 'inline' | 'stacked';
    alignment?: 'left' | 'center' | 'right';
    searchBarPosition?: 'top' | 'bottom';
    search_bar_position?: 'top' | 'bottom';
    unhideParent?: boolean;
    unhide_parent?: boolean;
    zIndex?: number;
    z_index?: number;
}

/** Localized placeholder text structure stored in database */
interface SearchBarPlaceholderJson {
    en?: string;
    fr?: string;
    de?: string;
    es?: string;
    it?: string;
}

/** Localized questions structure stored in database */
interface LocalizedQuestionsJson {
    en?: string[];
    fr?: string[];
    de?: string[];
    es?: string[];
    it?: string[];
}

/** Localized title structure stored in database */
interface LocalizedTitleJson {
    en?: string;
    fr?: string;
    de?: string;
    es?: string;
    it?: string;
}

/**
 * Extract questions for a specific language with fallback to English
 * Supports both old format (string[]) and new localized format (LocalizedQuestionsJson)
 */
function getLocalizedQuestions(
    rawQuestions: string[] | LocalizedQuestionsJson,
    language?: string
): string[] {
    // Old format: direct string array
    if (Array.isArray(rawQuestions)) {
        return rawQuestions;
    }

    // New format: localized object with language keys
    const lang = language || 'en';
    const localizedQuestions = rawQuestions as LocalizedQuestionsJson;

    // Try requested language, fallback to English, then any available language
    return (
        localizedQuestions[lang as keyof LocalizedQuestionsJson] ||
        localizedQuestions.en ||
        Object.values(localizedQuestions).find((q) => q && q.length > 0) ||
        []
    );
}

/**
 * Extract title for a specific language with fallback to English
 * Supports both old format (string) and new localized format (LocalizedTitleJson)
 */
function getLocalizedTitle(rawTitle: string | LocalizedTitleJson, language?: string): string {
    // Old format: direct string
    if (typeof rawTitle === 'string') {
        return rawTitle;
    }

    // New format: localized object with language keys
    const lang = language || 'en';
    const localizedTitle = rawTitle as LocalizedTitleJson;

    // Try requested language, fallback to English, then any available language
    return (
        localizedTitle[lang as keyof LocalizedTitleJson] ||
        localizedTitle.en ||
        Object.values(localizedTitle).find((t) => t?.trim()) ||
        ''
    );
}

/**
 * Transform a database row to an AISectionConfig
 *
 * @param row - The database row from config.ai_sections table
 * @param language - Optional language code for localized questions (default: 'en')
 */
function transformRowToConfig(row: AISectionRow, language?: string): AISectionConfig {
    // Cast JSON fields to their expected types
    // Title can be either old format (string) or new localized format (LocalizedTitleJson)
    const rawTitle = row.title as string | LocalizedTitleJson;
    const title = getLocalizedTitle(rawTitle, language);
    // Questions can be either old format (string[]) or new localized format (LocalizedQuestionsJson)
    const rawQuestions = row.questions as string[] | LocalizedQuestionsJson;
    const questions = getLocalizedQuestions(rawQuestions, language);
    const stylingJson = row.styling as StylingJson | null;
    const searchBarPlaceholder = row.search_bar_placeholder as SearchBarPlaceholderJson | null;

    // Parse styling from database JSON (supports both camelCase and snake_case)
    const styling: AISectionStyling | undefined = stylingJson
        ? {
              primaryColor: stylingJson.primaryColor || stylingJson.primary_color,
              backgroundColor: stylingJson.backgroundColor || stylingJson.background_color,
              buttonBackground: stylingJson.buttonBackground || stylingJson.button_background,
              buttonTextColor: stylingJson.buttonTextColor || stylingJson.button_text_color,
              buttonFontSize: stylingJson.buttonFontSize || stylingJson.button_font_size,
              buttonFontWeight: stylingJson.buttonFontWeight || stylingJson.button_font_weight,
              iconSize: stylingJson.iconSize ?? stylingJson.icon_size,
              arrowSize: stylingJson.arrowSize ?? stylingJson.arrow_size,
              titleColor: stylingJson.titleColor || stylingJson.title_color,
              gap: stylingJson.gap,
              layout: stylingJson.layout,
              alignment: stylingJson.alignment,
              searchBarPosition: stylingJson.searchBarPosition || stylingJson.search_bar_position,
              unhideParent: stylingJson.unhideParent ?? stylingJson.unhide_parent,
              zIndex: stylingJson.zIndex ?? stylingJson.z_index,
          }
        : undefined;

    // Context uses the same localized format as title
    const rawContext = row.context as string | LocalizedTitleJson | null;
    const context = rawContext ? getLocalizedTitle(rawContext, language) : undefined;

    return {
        title,
        questions,
        styling,
        customCSS: row.custom_css || undefined,
        showSearchBar: row.show_search_bar ?? false,
        searchBarPlaceholder: searchBarPlaceholder || undefined,
        hidesMainBar: row.hides_main_bar ?? undefined,
        context: context || undefined,
    };
}

/**
 * Transform a database row to an AISectionWithMeta (includes section_id and enabled)
 *
 * @param row - The database row from config.ai_sections table
 * @param language - Optional language code for localized questions (default: 'en')
 */
function transformRowToConfigWithMeta(row: AISectionRow, language?: string): AISectionWithMeta {
    const base = transformRowToConfig(row, language);
    return {
        ...base,
        sectionId: row.section_id,
        enabled: row.enabled ?? true,
    };
}
